import { Injectable } from '@angular/core';
import { ZipFileEntry } from '../shared/models';

export interface FolderStatsCacheEntry {
  syncId: number;
  cachekey?: string;
  totalBytes: number;
  fileCount: number;
  folderCount: number;
  failedPaths: string[];
  entries?: ZipFileEntry[];
  timestamp: number;
}

const TTL_MS = 2 * 60 * 1000;
const MAX_ENTRIES = 100;

@Injectable({
  providedIn: 'root',
})
export class FolderStatsCacheService {
  // Map iteration order is insertion order; re-inserting a key moves it to the end,
  // giving us LRU-by-write semantics when combined with the MAX_ENTRIES cap.
  private readonly store = new Map<number, FolderStatsCacheEntry>();

  public get(syncId: number, cachekey?: string): FolderStatsCacheEntry | null {
    const entry = this.store.get(syncId);
    if (!entry) {
      return null;
    }
    if (Date.now() - entry.timestamp >= TTL_MS) {
      this.store.delete(syncId);
      return null;
    }
    if (cachekey && entry.cachekey && cachekey !== entry.cachekey) {
      this.store.delete(syncId);
      return null;
    }
    return entry;
  }

  public set(entry: FolderStatsCacheEntry): void {
    const statsEntry: FolderStatsCacheEntry = {
      ...entry,
      timestamp: entry.timestamp != null ? entry.timestamp : Date.now(),
    };
    // Re-insert to move to most-recent end of iteration order
    this.store.delete(statsEntry.syncId);
    this.store.set(statsEntry.syncId, statsEntry);
    if (this.store.size > MAX_ENTRIES) {
      const oldestKey = this.store.keys().next().value as number;
      this.store.delete(oldestKey);
    }
  }

  public clear(): void {
    this.store.clear();
  }
}
